// queries/session.queries.ts
import { inject } from '@angular/core';
import { injectMutation, injectQuery, QueryClient } from '@tanstack/angular-query-experimental';
import { lastValueFrom } from 'rxjs';
import { IDENTITY_KEYS } from '../identity.query-keys';
import { NAVIGATION_KEYS } from '@features/navigation/navigation.query-keys';
import { AuthService } from '../services/auth.service';
import type { LoginRequest } from '../models/session.model';
import { Router } from '@angular/router';
import { getApiData } from '@shared/utils/api.utils';
import { preventSilentAccess, storeCredential } from '@shared/utils/password-credentials';
import type { ApiResponse } from '@shared/models/api.model';
import type { WebUsuario } from '@features/identity/models/usuario.model';

export function injectSessionQuery() {
    const authService = inject(AuthService);

    return injectQuery<ApiResponse<WebUsuario>, Error, WebUsuario>(() => ({
        queryKey: IDENTITY_KEYS.session(),
        queryFn: () => lastValueFrom(authService.whoami()),
        retry: false,
        staleTime: 1000 * 60 * 5, // 5 min — la cookie de Spring es la fuente de verdad
        select: getApiData,
    }));
}

export function injectLoginMutation() {
    const authService = inject(AuthService);
    const queryClient = inject(QueryClient);
    const router = inject(Router);

    return injectMutation(() => ({
        mutationFn: (body: LoginRequest) => lastValueFrom(authService.login(body)),
        onSuccess: async (data, body) => {
            // Offer to save the working credentials while we're still on the login page.
            await storeCredential(body.username, body.password);
            // Guardamos el usuario directamente en cache sin refetch
            await queryClient.cancelQueries({ queryKey: IDENTITY_KEYS.session() });
            queryClient.setQueryData(IDENTITY_KEYS.session(), data);
            await queryClient.invalidateQueries({ queryKey: NAVIGATION_KEYS.all() });
            // Let the session signal catch up before authGuard reads it, or navigate() gets cancelled.
            await new Promise((resolve) => setTimeout(resolve, 0));
            await router.navigate(['/app/dashboard']);
        },
    }));
}

export function injectLogoutMutation() {
    const authService = inject(AuthService);
    const queryClient = inject(QueryClient);
    const router = inject(Router);

    return injectMutation(() => ({
        mutationFn: () => lastValueFrom(authService.logout()),
        onSuccess: async () => {
            // Don't let the browser silently sign the user back in right after they logged out.
            await preventSilentAccess();
            queryClient.setQueryData(IDENTITY_KEYS.session(), null); // isAuthenticated → false
            queryClient.removeQueries({ queryKey: NAVIGATION_KEYS.all() });
            queryClient.removeQueries({
                predicate: (query) =>
                    query.queryKey[0] === IDENTITY_KEYS.session()[0] &&
                    query.queryKey[1] !== IDENTITY_KEYS.session()[1],
            });
            await lastValueFrom(authService.csrf());
            await router.navigate(['/iniciar-sesion']);
        },
    }));
}
